import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// Define empty template rows (20 empty rows for user to fill)
const initialRows = Array.from({ length: 20 }, (_, i) => ({
  no: i + 1,
  ia_name: '',
  municipality: '',
  districtNum: '',
  mailing_address: '',
  ia_president: '',
  contactNum: '',
  dateOrganized: '', // ✅ Included
  secNum: '',
  secDate: '',
  ia_tin: '',
  membership_potl: '',
  membership_actual: '',
  serviceArea: '',
  fusa: '',
  irrigated_area: '',
  operational_area: '',
}));

function NISTemplate({ darkMode = false }) {
  const [rows, setRows] = useState(initialRows);
  const [headerImo, setHeaderImo] = useState(localStorage.getItem('headerImo') || 'BOHOL-CEBU-SIQUIJOR IMO');
  const [headerProfileDate, setHeaderProfileDate] = useState(localStorage.getItem('headerProfileDate') || 'NIS IA PROFILE as of March 31, 2025');
  const [headerRegion, setHeaderRegion] = useState(localStorage.getItem('headerRegion') || 'Region: 7');
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const fileInputRef = useRef(null);

  const handleInputChange = (rowIndex, field, value) => {
    setRows(prev =>
      prev.map((row, i) =>
        i === rowIndex ? { ...row, [field]: value } : row
      )
    );
  };

  const saveHeader = () => {
    localStorage.setItem('headerImo', headerImo);
    localStorage.setItem('headerProfileDate', headerProfileDate);
    localStorage.setItem('headerRegion', headerRegion);
    setIsEditingHeader(false);
  };

  const handleBack = () => {
    // Tell DashboardAdmin to reopen the Template module when we return.
    localStorage.setItem('dashboardAdminInitialContent', 'Template');

    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    window.location.href = '/DashboardAdmin';
  };

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('NIS');

  // Header rows (use persisted header fields)
  worksheet.mergeCells('A1:Q1');
  worksheet.getCell('A1').value = headerImo;
  worksheet.getCell('A1').font = { name: 'Calibri', size: 18, bold: true };
  worksheet.getCell('A1').alignment = { horizontal: 'left', vertical: 'middle' };

  worksheet.mergeCells('A2:Q2');
  worksheet.getCell('A2').value = headerProfileDate;
  worksheet.getCell('A2').font = { name: 'Calibri', size: 18 };
  worksheet.getCell('A2').alignment = { horizontal: 'left', vertical: 'middle' };

  worksheet.mergeCells('A3:Q3');
  worksheet.getCell('A3').value = headerRegion;
  worksheet.getCell('A3').font = { name: 'Calibri', size: 18 };
  worksheet.getCell('A3').alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.addRow([]);

    // Main headers
    const mainHeaders = [
      'NO', 'NAME OF IRRIGATORS\' ASSOCIATION', 'MUNICIPALITY', 'DISTRICT NO.', 'MAILING ADDRESS',
      'NAME OF IA PRESIDENT', 'CONTACT NUMBER', 'DATE ORGANIZED', 'SEC / CDA/DOLE REG. NO.', '',
      'IA TIN', 'MEMBERSHIP', '', 'SERVICE AREA', 'FUSA', 'IRRIGATED AREA', 'OPERATIONAL AREA'
    ];
    const mainHeaderRow = worksheet.addRow(mainHeaders);
    mainHeaderRow.eachCell((cell, colNumber) => {
      cell.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E7D32' } };
      cell.alignment = { horizontal: 'center', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };
    });

    // Merge grouped headers
    worksheet.mergeCells(`I${mainHeaderRow.number}:J${mainHeaderRow.number}`); // SEC
    worksheet.mergeCells(`L${mainHeaderRow.number}:M${mainHeaderRow.number}`); // MEMBERSHIP

    // Subheaders
    const subHeaders = ['', '', '', '', '', '', '', '', 'NUMBER', 'DATE', '', 'POT\'L', 'ACTUAL', '', '', '', ''];
    const subHeaderRow = worksheet.addRow(subHeaders);
    subHeaderRow.eachCell((cell, colNumber) => {
      cell.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4CAF50' } };
      cell.alignment = { horizontal: 'center', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };
    });

    // Data rows
    rows.forEach(row => {
      const dataRow = worksheet.addRow([
        row.no,
        row.ia_name,
        row.municipality,
        row.districtNum,
        row.mailing_address,
        row.ia_president,
        row.contactNum,
        row.dateOrganized,
        row.secNum,
        row.secDate,
        row.ia_tin,
        row.membership_potl,
        row.membership_actual,
        row.serviceArea,
        row.fusa,
        row.irrigated_area,
        row.operational_area
      ]);
      dataRow.eachCell(cell => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } },
        };
      });
    });

    // Set column widths
    worksheet.columns = [
      { key: 'no', width: 5 },
      { key: 'ia_name', width: 30 },
      { key: 'municipality', width: 25 },
      { key: 'districtNum', width: 10 },
      { key: 'mailing_address', width: 25 },
      { key: 'ia_president', width: 25 },
      { key: 'contactNum', width: 25 },
      { key: 'dateOrganized', width: 15 },
      { key: 'secNum', width: 15 },
      { key: 'secDate', width: 10 },
      { key: 'ia_tin', width: 15 },
      { key: 'membership_potl', width: 10 },
      { key: 'membership_actual', width: 10 },
      { key: 'serviceArea', width: 15 },
      { key: 'fusa', width: 10 },
      { key: 'irrigated_area', width: 15 },
      { key: 'operational_area', width: 15 },
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `NIS_Template_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  // Scroll sync
  useEffect(() => {
    const scrollContent = document.getElementById('scrollContent');
    const bottomScroll = document.getElementById('bottomScroll');
    if (scrollContent && bottomScroll) {
      // ensure the bottom spacer matches the actual table width so the bottom scrollbar
      // spans the full width of the table. Update on init and on window resize.
      const updateBottomWidth = () => {
        try {
          // prefer table scrollWidth if available
          const innerTable = scrollContent.querySelector('table');
          const width = innerTable ? innerTable.scrollWidth : scrollContent.scrollWidth;
          const spacer = bottomScroll.firstElementChild;
          if (spacer) spacer.style.width = width + 'px';
        } catch (e) {
          // ignore
        }
      };
      updateBottomWidth();
      window.addEventListener('resize', updateBottomWidth);
      const onBottomScroll = () => {
        scrollContent.scrollLeft = bottomScroll.scrollLeft;
      };
      const onContentScroll = () => {
        bottomScroll.scrollLeft = scrollContent.scrollLeft;
      };
      bottomScroll.addEventListener('scroll', onBottomScroll);
      scrollContent.addEventListener('scroll', onContentScroll);
      return () => {
        bottomScroll.removeEventListener('scroll', onBottomScroll);
        scrollContent.removeEventListener('scroll', onContentScroll);
        window.removeEventListener('resize', updateBottomWidth);
      };
    }
  }, []);

  return (
    <Container darkMode={darkMode}>
      <TopControls>
        <BackButton onClick={handleBack}>Back</BackButton>
      </TopControls>
      <HeaderContainer>
        <HeaderLeft>
          <HeaderText>
            {isEditingHeader ? (
              <>
                <input value={headerImo} onChange={(e) => setHeaderImo(e.target.value)} style={{ width: '300px', marginBottom: '5px' }} />
                <input value={headerProfileDate} onChange={(e) => setHeaderProfileDate(e.target.value)} style={{ width: '300px', marginBottom: '5px' }} />
                <input value={headerRegion} onChange={(e) => setHeaderRegion(e.target.value)} style={{ width: '300px', marginBottom: '5px' }} />
                <button onClick={saveHeader} style={{ marginRight: '5px' }}>Save</button>
                <button onClick={() => setIsEditingHeader(false)}>Cancel</button>
              </>
            ) : (
              <>
                <h2>{headerImo}</h2>
                <p>{headerProfileDate}</p>
                <p>{headerRegion}</p>
                <p>PROVINCE 1: BOHOL</p>
              </>
            )}
          </HeaderText>
          {/* year selector removed per request */}
        </HeaderLeft>
       
      </HeaderContainer>
      <TableContainer>
        <TableWrapper>
          <div id="scrollContent" style={{ width: '100%', overflowX: 'auto' }}>
            <Table darkMode={darkMode}>
              <thead>
                <tr>
                  <TableHeader darkMode={darkMode}>ACTIONS</TableHeader>
                  <TableHeader darkMode={darkMode}>NO</TableHeader>
                  <TableHeader darkMode={darkMode}>NAME OF IRRIGATORS' ASSOCIATION</TableHeader>
                  <TableHeader darkMode={darkMode}>MUNICIPALITY</TableHeader>
                  <TableHeader darkMode={darkMode}>DISTRICT NO.</TableHeader>
                  <TableHeader darkMode={darkMode}>MAILING ADDRESS</TableHeader>
                  <TableHeader darkMode={darkMode}>NAME OF IA PRESIDENT</TableHeader>
                  <TableHeader darkMode={darkMode}>CONTACT NUMBER</TableHeader>
                  <TableHeader darkMode={darkMode}>DATE ORGANIZED</TableHeader> {/* ✅ Header present */}
                  <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                    SEC / CDA/DOLE REG. NO.
                  </TableHeader>
                  <TableHeader darkMode={darkMode}>IA TIN</TableHeader>
                  <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                    MEMBERSHIP
                  </TableHeader>
                  <TableHeader darkMode={darkMode}>SERVICE AREA</TableHeader>
                  <TableHeader darkMode={darkMode}>FUSA</TableHeader>
                  <TableHeader darkMode={darkMode}>IRRIGATED AREA</TableHeader>
                  <TableHeader darkMode={darkMode}>OPERATIONAL AREA</TableHeader>
                </tr>
                <tr>
                  <TableHeaderS darkMode={darkMode}></TableHeaderS>
                  <TableHeaderS darkMode={darkMode}></TableHeaderS>
                  <TableHeaderS darkMode={darkMode}></TableHeaderS>
                  <TableHeaderS darkMode={darkMode}></TableHeaderS>
                  <TableHeaderS darkMode={darkMode}></TableHeaderS>
                  <TableHeaderS darkMode={darkMode}></TableHeaderS>
                  <TableHeaderS darkMode={darkMode}></TableHeaderS>
                  <TableHeaderS darkMode={darkMode}></TableHeaderS>
                  <TableHeaderS darkMode={darkMode}></TableHeaderS> {/* ✅ Subheader placeholder */}
                  <TableHeaderS darkMode={darkMode}>NUMBER</TableHeaderS>
                  <TableHeaderS darkMode={darkMode}>DATE</TableHeaderS>
                  <TableHeaderS darkMode={darkMode}></TableHeaderS>
                  <TableHeaderS darkMode={darkMode}>POT'L</TableHeaderS>
                  <TableHeaderS darkMode={darkMode}>ACTUAL</TableHeaderS>
                  <TableHeaderS darkMode={darkMode}></TableHeaderS>
                  <TableHeaderS darkMode={darkMode}></TableHeaderS>
                  <TableHeaderS darkMode={darkMode}></TableHeaderS>
                  <TableHeaderS darkMode={darkMode}></TableHeaderS>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan="18" style={{ backgroundColor: '#cadaba', fontWeight: 'bold', textAlign: 'left', paddingLeft: '10px' }}>
                    GRAVITY
                  </td>
                </tr>
                {rows.map((row, rowIndex) => (
                  <TableRow key={rowIndex} darkMode={darkMode}>
                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '60px', padding: '8px 0' }} />
                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '40px' }}>
                      {row.no}
                    </TableData>
                    <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '240px' }}>
                      <Input value={row.ia_name} onChange={(e) => handleInputChange(rowIndex, 'ia_name', e.target.value)} />
                    </TableData>
                    <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px' }}>
                      <Input value={row.municipality} onChange={(e) => handleInputChange(rowIndex, 'municipality', e.target.value)} />
                    </TableData>
                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                      <Input value={row.districtNum} onChange={(e) => handleInputChange(rowIndex, 'districtNum', e.target.value)} />
                    </TableData>
                    <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px' }}>
                      <Input value={row.mailing_address} onChange={(e) => handleInputChange(rowIndex, 'mailing_address', e.target.value)} />
                    </TableData>
                    <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px' }}>
                      <Input value={row.ia_president} onChange={(e) => handleInputChange(rowIndex, 'ia_president', e.target.value)} />
                    </TableData>
                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '200px' }}>
                      <Input value={row.contactNum} onChange={(e) => handleInputChange(rowIndex, 'contactNum', e.target.value)} />
                    </TableData>
                    {/* ✅ DATE ORGANIZED INPUT FIELD — THIS WAS MISSING */}
                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                      <Input
                        value={row.dateOrganized}
                        onChange={(e) => handleInputChange(rowIndex, 'dateOrganized', e.target.value)}
                      />
                    </TableData>
                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                      <Input value={row.secNum} onChange={(e) => handleInputChange(rowIndex, 'secNum', e.target.value)} />
                    </TableData>
                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                      <Input value={row.secDate} onChange={(e) => handleInputChange(rowIndex, 'secDate', e.target.value)} />
                    </TableData>
                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                      <Input value={row.ia_tin} onChange={(e) => handleInputChange(rowIndex, 'ia_tin', e.target.value)} />
                    </TableData>
                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                      <Input value={row.membership_potl} onChange={(e) => handleInputChange(rowIndex, 'membership_potl', e.target.value)} />
                    </TableData>
                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                      <Input value={row.membership_actual} onChange={(e) => handleInputChange(rowIndex, 'membership_actual', e.target.value)} />
                    </TableData>
                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                      <Input value={row.serviceArea} onChange={(e) => handleInputChange(rowIndex, 'serviceArea', e.target.value)} />
                    </TableData>
                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                      <Input value={row.fusa} onChange={(e) => handleInputChange(rowIndex, 'fusa', e.target.value)} />
                    </TableData>
                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                      <Input value={row.irrigated_area} onChange={(e) => handleInputChange(rowIndex, 'irrigated_area', e.target.value)} />
                    </TableData>
                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                      <Input value={row.operational_area} onChange={(e) => handleInputChange(rowIndex, 'operational_area', e.target.value)} />
                    </TableData>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </div>
        </TableWrapper>
        <div id="bottomScroll" style={{ height: '20px', overflowX: 'auto', overflowY: 'hidden', width: '100%' }}>
          <div style={{ width: '2120px', height: '1px' }} />
        </div>
      </TableContainer>
      <ExportButton onClick={exportToExcel} style={{ marginLeft: '10px' }}>📤 Export Template</ExportButton>
    </Container>
  );
}

export default NISTemplate;

// Styled Components (same as before, no adminBg)
const Container = styled.div`
  background-color: ${({ darkMode }) => (darkMode ? '#1a1a1a' : '#f2f2f2')};
  color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#333')};
  padding: 80px 20px 20px;
  width: 98%;
  min-height: 100vh;
  overflow-y: auto;
  margin-left: 10px;
  box-shadow: -4px 0 10px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0 40px;
`;

const TopControls = styled.div`
  display: flex;
  justify-content: flex-start;
  padding: 0 40px;
  margin-bottom: 16px;
`;

const BackButton = styled.button`
  margin: 0 2px;
  padding: 6px 12px;
  background-color: #eeeeee;
  color: #222;
  border: 1px solid #b0b0b0;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: #e2e2e2;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const HeaderText = styled.div`
  margin-bottom: 10px;
  margin-top: 0;
  h2 {
    margin: 0;
    font-size: 22px;
    color: ${({ darkMode }) => (darkMode ? '#ffffff' : '#333')};
  }
  p {
    margin: 0;
    font-size: 18px;
    color: ${({ darkMode }) => (darkMode ? '#cccccc' : '#555')};
  }
`;

const YearGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RightControls = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #4caf50;
  border-radius: 4px;
  background-color: #ffffff;
  color: #333;
  font-size: 14px;
  width: 200px;
`;

const StyledButton = styled.button`
  padding: 6px 10px;
  background-color: #eee;
  border: 1px solid #aaa;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  &:hover {
    background-color: #ddd;
  }
`;

const ManageDataSelect = styled.select`
  padding: 6px 10px;
  background-color: #eee;
  border: 1px solid #aaa;
  border-radius: 4px;
  color: #333;
  cursor: pointer;
  font-size: 14px;
  &:hover {
    background-color: #ddd;
  }
`;

const TableContainer = styled.div`
  margin-top: 20px;
  width: 100%;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: hidden; /* hide the main wrapper scrollbar - use bottomScroll as visible scrollbar */
  overflow-y: visible; /* allow vertical scrolling inside the inner scroll area */
  max-height: 600px;
  border: 1px solid #ccc;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari */
  }

  /* keep the inner scrollContent scrollable vertically but hide its visual scrollbar */
  #scrollContent {
    width: 100%;
    overflow-x: auto;
    overflow-y: auto; /* allow vertical scrolling inside the table area */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE 10+ */
  }

  #scrollContent::-webkit-scrollbar {
    display: none; /* Chrome, Safari */
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 2120px;
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
`;

const TableHeader = styled.th`
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: ${({ darkMode }) => (darkMode ? '#333' : '#2e7d32')};
  color: white;
  padding: 10px;
  font-size: 14px;
  border: 1px solid #ddd;
  text-align: center;
  vertical-align: middle;
  &:first-child {
    position: sticky;
    left: 0;
    z-index: 3;
    background-color: ${({ darkMode }) => (darkMode ? '#333' : '#2e7d32')};
  }
`;

const TableHeaderS = styled.th`
  position: sticky;
  top: 39px;
  z-index: 1;
  background-color: ${({ darkMode }) => (darkMode ? '#222' : '#4caf50')};
  color: white;
  padding: 0 10px;
  border: 1px solid #ddd;
  font-size: 12px;
  text-align: center;
  &:first-child {
    position: sticky;
    left: 0;
    z-index: 3;
    background-color: ${({ darkMode }) => (darkMode ? '#222' : '#4caf50')};
  }
`;

const TableRow = styled.tr`
  background: ${({ darkMode }) => (darkMode ? '#2a2a2a' : '#fff')};
  &:hover {
    background-color: ${({ darkMode }) => (darkMode ? '#333' : '#f5f5f5')};
  }
`;

const TableData = styled.td`
  padding: 5px;
  font-size: 12px;
  border: 1px solid #ddd;
  color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#333')};
  &:first-child {
    position: sticky;
    left: 0;
    background-color: ${({ darkMode }) => (darkMode ? '#2a2a2a' : '#fff')};
    z-index: 1;
  }
`;

const ActionButton = styled.button`
  width: 40px;
  height: 40px;
  background: #fff;
  border: 2px solid #ddd;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 4px;
  cursor: pointer;
  font-size: 22px;
  transition: border-color 0.2s;
  &:hover {
    border-color: #2e7d32;
    background: #f5f5f5;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

const Input = styled.input`
  width: 100%;
  border: none;
  padding: 4px;
  box-sizing: border-box;
  font-size: 12px;
  background: transparent;
  text-align: center;
  &:focus {
    outline: none;
    background-color: #f0f8ff;
  }
`;

/* YearSelect removed — no year dropdown in header */

const ExportButton = styled.button`
  margin: 24px auto 0 auto;
  display: block;
  padding: 8px 16px;
  background-color: #2e7d32;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 15px;
  font-weight: bold;
  &:hover {
    background-color: #276a2b;
  }
`;